import { useCallback, useEffect, useRef } from "react"

import { AudioVideoFacade } from "amazon-chime-sdk-js"

import { SoundTouchNode } from "@soundtouchjs/audio-worklet"

import airbrake from "src/airbrake-notifier"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - file-loader (see environment.js) transforms this import to a URL string at build time
import soundtouchProcessorUrl from "./helpers/soundtouch-speech-processor.js"

import useChimeMeetingAudioStream from "./useChimeMeetingAudioStream"
import useMeetingAudioGraph from "./useMeetingAudioGraph"

type UsePlaybackRateProps = {
  audioVideo?: AudioVideoFacade
  playbackRate: number
  instructorAttendeeId?: string
}

// The instructorAttendeeId, usually a teacher or tutor, references the primary speaker in the meeting. Their speech is
// considered to be the "most important" audio.

// In order to prevent the audio stream from being slowed indefinitely, forcing the listener further and further
// behind, we use a buffer which will fill with audio only while the instructorAttendee is speaking and then output that
// audio at a slowed rate. Once the instructorAttendee stops speaking and the buffer has fully emptied, the room audio will
// be played in real-time speed until the instructorAttendee begins speaking again, at which point the buffer will begin to
// fill again.
// Note that this may result in occasional data loss of any audio other than the instructorAttendee's audio.

const usePlaybackRate = ({
  audioVideo,
  playbackRate,
  instructorAttendeeId, // see note above for context
}: UsePlaybackRateProps) => {
  const meetingAudioStream = useChimeMeetingAudioStream(audioVideo)
  const { setupGraph, teardownGraph } = useMeetingAudioGraph()
  const soundTouchNodeRef = useRef<SoundTouchNode | null>(null)

  const teardownPlaybackRate = useCallback(() => {
    soundTouchNodeRef.current?.disconnect()
    soundTouchNodeRef.current = null
    teardownGraph()

    const roomAudio = document.querySelector<HTMLAudioElement>("#room-audio")
    if (roomAudio) roomAudio.muted = false
  }, [teardownGraph])

  useEffect(() => {
    if (playbackRate === 1) return // feature not enabled
    if (!audioVideo || !meetingAudioStream) return // cloudroom not ready

    let cancelled = false

    const setupPlaybackRate = async () => {
      const roomAudio = document.querySelector<HTMLAudioElement>("#room-audio")
      if (roomAudio) roomAudio.muted = true // Mute the audio element so only the processed Web Audio output is heard.

      const graph = await setupGraph(meetingAudioStream)
      if (cancelled) {
        teardownPlaybackRate()
        return
      }

      await SoundTouchNode.register(graph.context, soundtouchProcessorUrl)

      const soundTouchNode = new SoundTouchNode(graph.context)
      graph.source.connect(soundTouchNode)
      soundTouchNode.connect(graph.context.destination)

      soundTouchNode.tempo.value = playbackRate
      soundTouchNode.pitch.value = 1
      soundTouchNode.playbackRate.value = 1

      soundTouchNodeRef.current = soundTouchNode
    }

    setupPlaybackRate().catch(error => {
      if (cancelled) return
      airbrake.notify({
        message: "usePlaybackRate setup failed",
        params: { name: "usePlaybackRate" },
        error,
      })
      teardownPlaybackRate()
    })

    return () => {
      cancelled = true
      teardownPlaybackRate()
    }
  }, [
    audioVideo,
    meetingAudioStream,
    playbackRate,
    setupGraph,
    teardownPlaybackRate,
  ])

  // Subscribe to Chime's volume indicator for the instructorAttendeeId and relay
  // isSpeaking to the AudioWorklet processor via its message port.
  useEffect(() => {
    if (!audioVideo || !instructorAttendeeId || playbackRate >= 1) return

    const SILENCE_HOLD_MS = 750 // duration volume must stay at zero before we declare the instructorAttendee not speaking.

    let silenceTimeout: NodeJS.Timeout | null = null

    const handleVolumeIndicator = (
      _attendeeId: string,
      volume: number | null
    ) => {
      if (volume === null) return
      const soundTouchNode = soundTouchNodeRef.current
      if (!soundTouchNode) return

      if (volume > 0) {
        clearTimeout(silenceTimeout)
        silenceTimeout = null
        soundTouchNode.port.postMessage({ isSpeaking: true })
        return
      }
      if (silenceTimeout !== null) return
      silenceTimeout = setTimeout(() => {
        silenceTimeout = null
        soundTouchNodeRef.current?.port.postMessage({ isSpeaking: false })
      }, SILENCE_HOLD_MS)
    }

    audioVideo.realtimeSubscribeToVolumeIndicator(
      instructorAttendeeId,
      handleVolumeIndicator
    )

    return () => {
      if (silenceTimeout !== null) clearTimeout(silenceTimeout)
      audioVideo.realtimeUnsubscribeFromVolumeIndicator(
        instructorAttendeeId,
        handleVolumeIndicator
      )
    }
  }, [audioVideo, instructorAttendeeId, playbackRate])
}

export default usePlaybackRate
